import type { ColDef } from 'ag-grid-community'
import { startCase } from 'lodash'

import { endOfTime } from '../../../types'
import { EditActionButton } from './CellRenderers/EditActionButton'
import { NotesRenderer } from './CellRenderers/NotesRenderer'
import { ScheduleRenderer } from './CellRenderers/ScheduleRenderer'
import { StatusRenderer } from './CellRenderers/StatusRenderer'
import dayjs from 'dayjs'
import { startEndFormatter } from '../../../util'
import { ShortUpdatedRenderer } from '@components/_SiteWide/Grid/CellRenderers/ShortUpdatedRenderer'

type EditAction = (supplyOutage: Gravitate.SupplyOutage) => void

export function getColumnDefs(handleEditOutage: EditAction): ColDef<Gravitate.SupplyOutage>[] {
  return [
    {
      field: 'status',
      headerName: 'Status',
      sort: 'asc',
      cellRenderer: StatusRenderer,
    },
    { field: 'reason_code', headerName: 'Reason' },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      cellRenderer: NotesRenderer,
      enableRowGroup: false,
    },
    {
      field: 'schedule_local.schedule_type',
      headerName: 'Schedule',
      cellRenderer: ScheduleRenderer,
      valueGetter: ({ data }) => startCase(data.schedule_local.schedule_type),
    },
    {
      headerName: 'Start',
      field: 'schedule_local.start_date',
      cellRenderer: ({ data, value }) => startEndFormatter(data.schedule_local.schedule_type, value, data.timezone),
      filter: 'agDateColumnFilter',
      filterValueGetter: ({ data }) => {
        if (!data?.schedule_local?.start_date) return null
        return dayjs(data.schedule_local.start_date).startOf('day').toDate()
      },
      enableRowGroup: false,
    },
    {
      headerName: 'End',
      field: 'schedule_local.end_date',
      cellRenderer: ({ data, value }) => startEndFormatter(data.schedule_local.schedule_type, value, data.timezone),
      filter: 'agDateColumnFilter',
      filterValueGetter: ({ data }) => {
        if (!data?.schedule_local?.end_date) return null
        return dayjs(data.schedule_local.end_date).startOf('day').toDate()
      },
      enableRowGroup: false,
      comparator: (valueA, valueB) => {
        // Handle null values - put them at the end
        if (valueA === null && valueB === null) return 0
        if (valueA === null) return 1 // null goes after non-null
        if (valueB === null) return -1 // non-null goes before null

        // Both values are non-null dates, compare normally
        return dayjs(valueA).unix() - dayjs(valueB).unix()
      },
    },
    {
      field: 'timezone',
    },
    {
      field: 'terminal_name',
      headerName: 'Terminal',
    },
    {
      field: 'product_group',
      headerName: 'Product Group',
      valueGetter: ({ data }) => data.product_group || 'All',
    },
    {
      field: 'product_name',
      headerName: 'Product',
      valueGetter: ({ data }) => data.product_name || 'All',
    },
    {
      field: 'supplier_name',
      headerName: 'Supplier',
      valueGetter: ({ data }) => data.supplier_name || 'All',
    },
    {
      field: 'price_type',
      headerName: 'Price Type',
      valueGetter: ({ data }) => startCase(data.price_type) || 'All',
    },
    {
      field: 'contract',
      headerName: 'Contract',
      valueGetter: ({ data }) => data.contract || 'All',
    },
    {
      field: 'supply_owner_name',
      enableRowGroup: true,
      headerName: 'Supply Owner',
      valueGetter: ({ data }) => data.supply_owner_name || 'All',
    },
    {
      field: 'updated_on',
      headerName: 'Updated',
      cellRenderer: ShortUpdatedRenderer,
      minWidth: 100,
      filterValueGetter: ({ data }) => data.updated_by,
    },
    {
      headerName: 'Actions',
      enableRowGroup: false,
      cellRenderer: (params) => <EditActionButton handleEdit={() => handleEditOutage(params.data)} />,
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      sortable: false,
    },
  ]
}
